import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import Loader from "./components/loader";
import { routeTree } from "./routeTree.gen";
import { orpc, queryClient } from "./utils/orpc";
import * as UIReact from "tinybase/ui-react/with-schemas";
import { Provider as TinyBaseProvider } from "tinybase/ui-react";
import { Provider as TinyTickProvider, useCreateManager } from "tinytick/ui-react";
import { createManager } from "tinytick";
import { Inspector } from "tinybase/ui-react-inspector";
import { createStore } from "tinybase/with-schemas";
import { useNetworkState } from "@uidotdev/usehooks";

import { AuthStore } from "@/stores/auth-store";
import { UserStore } from "@/stores/user-store";
import { AppStore } from "@/stores/app-store";
import { DemoStore } from "@/stores/demo-store";
import { OutgrowerMonitoringStore } from "@/stores/outgrower-monitoring-store";

import {
    AUTH_VALUES_SCHEMA,
    type AuthSchema,
    USER_VALUES_SCHEMA,
    type UserSchema,
    APP_VALUES_SCHEMA,
    type AppSchema,
} from "@/stores/schemas";
import { db } from "./stores/indexed-db";

export const {
    useCreateStore: useCreateAuthStore,
    useStore: useAuthStore,
} = UIReact as UIReact.WithSchemas<AuthSchema>;

export const {
    useCreateStore: useCreateUserStore,
    useStore: useUserStore,
} = UIReact as UIReact.WithSchemas<UserSchema>;

export const {
    useCreateStore: useCreateAppStore,
    useStore: useAppStore,
} = UIReact as UIReact.WithSchemas<AppSchema>;

const router = createRouter({
    routeTree,
    defaultPreload: "intent",
    defaultPreloadDelay: 500,
    scrollRestoration: true,
    scrollRestorationBehavior: "smooth",
    defaultPendingComponent: () => <Loader />,
    context: { orpc, queryClient, db, auth: undefined!, user: undefined!, manager: undefined!, network: undefined!, app: undefined! },
    Wrap: function WrapComponent({ children }) {
        return (
            <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
        );
    },
});

// Register things for typesafety
declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}


export const App = () => {
    const authStore = useCreateAuthStore(() => createStore().setValuesSchema(AUTH_VALUES_SCHEMA), []);
    const userStore = useCreateUserStore(() => createStore().setValuesSchema(USER_VALUES_SCHEMA), []);
    const appStore = useCreateAppStore(() => createStore().setValuesSchema(APP_VALUES_SCHEMA), []);

    const taskManager = useTaskManager();
    const network = useNetworkState();

    return (
        <TinyTickProvider manager={taskManager}>
            <TinyBaseProvider>
                <AuthStore authStore={authStore} userStore={userStore} />
                <UserStore store={userStore} />
                <AppStore store={appStore} />
                <DemoStore />
                <OutgrowerMonitoringStore />
                <RouterProvider router={router} context={{ orpc, queryClient, auth: authStore, user: userStore, manager: taskManager, network, app: appStore, db }} />
                {import.meta.env.VITE_NODE_ENV === "development" && <Inspector />}
            </TinyBaseProvider>
        </TinyTickProvider>
    );
}

const useTaskManager = () => {
    return useCreateManager(() => {
        console.log("[tasks] Creating Task Manager");

        const manager = createManager().start();

        manager.addTaskRunFailedListener(
            null,
            null,
            (_, taskID, taskRunID, reason, message) =>
                console.error(`Task ${taskID}/${taskRunID} failed with reason ${reason}: ${message}`)
        );

        return manager;
    });
}